import { useState } from 'react'
import { useFacility, useFacilityPages } from '../index.js'
import { useDebouncedValue } from '../../roster/index.js'
import { LoadFailure } from '../../core/index.js'

/** How many facilities the picker lists before asking for more; a search narrows it instead of scrolling through hundreds. */
const PICKER_PAGE_SIZES = { first: 20, more: 20 } as const
/** Below this many facilities they all fit in view, so there is nothing to search for. */
const SEARCH_FROM_COUNT = 9

interface FacilityPickerProps {
  selectedFacilityIds: readonly string[]
  onToggle: (facilityId: string) => void
}

/** A chosen facility that is not in the list on screen - a later page, or filtered out by the search - shown by name so it can still be seen and unticked. */
function SelectedElsewhere({ facilityId, onToggle }: { facilityId: string; onToggle: (facilityId: string) => void }) {
  const { data } = useFacility(facilityId)
  return (
    <label className="flex items-center gap-1.5 text-xs text-ink">
      <input type="checkbox" checked onChange={() => onToggle(facilityId)} />
      {data?.name ?? facilityId}
    </label>
  )
}

/**
 * Ticks facilities out of all the client has. The facilities are loaded a
 * page at a time and searched by the start of the name, so choosing among
 * hundreds needs neither the whole list nor a long scroll; every facility
 * already chosen stays on show, whether or not it is in the page loaded.
 */
export function FacilityPicker({ selectedFacilityIds, onToggle }: FacilityPickerProps) {
  const [text, setText] = useState('')
  const search = useDebouncedValue(text)
  const pagesQuery = useFacilityPages(search, PICKER_PAGE_SIZES)
  const facilities = pagesQuery.data?.pages.flatMap((page) => page.items) ?? []
  const total = pagesQuery.data?.pages[0]?.total ?? facilities.length
  const listedIds = new Set(facilities.map((facility) => facility.facilityId))
  const selectedElsewhere = selectedFacilityIds.filter((facilityId) => !listedIds.has(facilityId))
  const isSearching = search.trim() !== ''

  return (
    <div className="flex flex-col gap-2">
      {(isSearching || total >= SEARCH_FROM_COUNT) && (
        <input
          type="search"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Find a facility by the start of its name"
          aria-label="Find a facility"
          autoComplete="off"
          className="border border-marine/20 px-2 py-1.5 text-xs max-w-xs"
        />
      )}
      {selectedElsewhere.length > 0 && (
        <div className="flex flex-wrap gap-3" aria-label="Chosen facilities not in the list below">
          {selectedElsewhere.map((facilityId) => (
            <SelectedElsewhere key={facilityId} facilityId={facilityId} onToggle={onToggle} />
          ))}
        </div>
      )}
      {pagesQuery.isPending ? (
        <span className="text-[11px] text-ink-mid">Loading facilities…</span>
      ) : pagesQuery.isError ? (
        <LoadFailure size="xs" message="Failed to load facilities." onRetry={() => void pagesQuery.refetch()} retrying={pagesQuery.isFetching} />
      ) : (
        <div className="flex flex-wrap gap-3">
          {facilities.map((facility) => (
            <label key={facility.facilityId} className="flex items-center gap-1.5 text-xs text-ink">
              <input type="checkbox" checked={selectedFacilityIds.includes(facility.facilityId)} onChange={() => onToggle(facility.facilityId)} />
              {facility.name}
            </label>
          ))}
          {facilities.length === 0 && <span className="text-[11px] text-ink-mid">{isSearching ? 'No facility name starts with that.' : 'No facilities.'}</span>}
        </div>
      )}
      {pagesQuery.hasNextPage && (
        <button type="button" onClick={() => void pagesQuery.fetchNextPage()} disabled={pagesQuery.isFetchingNextPage} className="self-start text-[11px] font-bold text-marine hover:text-marine-dk disabled:opacity-50">
          {pagesQuery.isFetchingNextPage ? 'Loading…' : `Show more (${facilities.length} of ${total})`}
        </button>
      )}
    </div>
  )
}
